import { API_BASE_URL, fetchApi } from '@/services/api';

import type {
  AssumptionDetail,
  CreateAssumptionPayload,
  UpdateAssumptionPayload,
} from '@/modules/demand-planning/types/consensus.types';

import { waitForRollupJob, type RollupJobStatus } from './rollupAssumptions';

export interface AssumptionMutationResponse {
  id?: number;
  rollup_job?: RollupJobStatus;
}

function buildAssumptionBody(payload: CreateAssumptionPayload, planningCycleId: number) {
  return {
    planning_cycle: planningCycleId,
    status: 'draft',
    requires_approval: true,
    title: payload.title,
    description: payload.description,
    assumption_types: payload.assumption_types,
    assumption_type: payload.assumption_type,
    severity: payload.severity,
    probability: payload.probability,
    start_bucket: payload.start_bucket,
    end_bucket: payload.end_bucket,
    impact_mode: payload.impact_mode,
    impacts_input: payload.impact_mode === 'manual' ? payload.impacts_input : [],
  };
}

async function authHeaders(): Promise<Headers> {
  const headers = new Headers({ 'Content-Type': 'application/json' });
  const token = localStorage.getItem('auth_token');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return headers;
}

async function deleteWithOptionalRollup(id: number, planningCycleId: number): Promise<void> {
  const headers = await authHeaders();
  const response = await fetch(`${API_BASE_URL}/demand-planning/assumptions/${id}/`, {
    method: 'DELETE',
    headers,
  });

  if (response.status === 409) {
    const data = await response.json();
    throw new Error(data.detail || 'Rollup in progress');
  }

  if (response.status === 202) {
    const data = (await response.json()) as AssumptionMutationResponse;
    await waitForRollupJob(planningCycleId, data.rollup_job);
    return;
  }

  if (!response.ok && response.status !== 204) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Delete failed');
  }
}

export const assumptionsService = {
  getById(id: number): Promise<AssumptionDetail> {
    return fetchApi<AssumptionDetail>(`demand-planning/assumptions/${id}/`);
  },

  async create(planningCycleId: number, payload: CreateAssumptionPayload): Promise<void> {
    const data = await fetchApi<AssumptionMutationResponse>(`demand-planning/assumptions/`, {
      method: 'POST',
      body: buildAssumptionBody(payload, planningCycleId),
    });
    await waitForRollupJob(planningCycleId, data.rollup_job);
  },

  async update(id: number, planningCycleId: number, payload: UpdateAssumptionPayload): Promise<void> {
    const data = await fetchApi<AssumptionMutationResponse>(`demand-planning/assumptions/${id}/`, {
      method: 'PATCH',
      body: buildAssumptionBody(payload, planningCycleId),
    });
    await waitForRollupJob(planningCycleId, data.rollup_job);
  },

  delete(id: number, planningCycleId: number): Promise<void> {
    return deleteWithOptionalRollup(id, planningCycleId);
  },
};
